import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LinkedIn as LinkedInIcon } from "@mui/icons-material";
import { useQuickAddLinkedIn } from "@/hooks/useCRM";

// Source-string vocabulary defined in
// docs/evelina-linkedin-source-conventions.md — keep these in lockstep.
const INTERACTIONS: { value: string; label: string }[] = [
  { value: "post_engagement", label: "They engaged with my post" },
  { value: "inbound_dm", label: "They sent me a DM" },
  { value: "outbound_dm", label: "I sent them a DM" },
  { value: "connection", label: "We connected" },
  { value: "profile_view", label: "They viewed my profile" },
  { value: "event", label: "Met at an event" },
];

const INTENTS: { value: string; label: string }[] = [
  { value: "audit", label: "Free audit" },
  { value: "webinar", label: "Webinar invite" },
  { value: "coaching", label: "Coaching consult" },
  { value: "keynote", label: "Keynote booking" },
  { value: "unsure", label: "Unsure — keep warm" },
];

interface FormState {
  first_name: string;
  last_name: string;
  linkedin_url: string;
  email: string;
  company_name: string;
  interaction: string;
  conversion_intent: string;
  note: string;
}

const EMPTY: FormState = {
  first_name: "",
  last_name: "",
  linkedin_url: "",
  email: "",
  company_name: "",
  interaction: "post_engagement",
  conversion_intent: "audit",
  note: "",
};

/**
 * Global floating-action button + modal for capturing a LinkedIn lead
 * in under 30 seconds. Mounted in Layout so it's reachable from any
 * page — Evelina's on her phone between meetings and the workflow has
 * to be a single-thumb flow.
 */
export default function LinkedInQuickAddFab() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const quickAdd = useQuickAddLinkedIn();

  const reset = () => {
    setForm(EMPTY);
    setSuccessMsg(null);
    quickAdd.reset();
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const canSubmit =
    form.first_name.trim().length > 0 && form.last_name.trim().length > 0 && !quickAdd.isPending;

  const handleSubmit = async () => {
    setSuccessMsg(null);
    const result = await quickAdd.mutateAsync({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      linkedin_url: form.linkedin_url.trim() || null,
      email: form.email.trim() || null,
      company_name: form.company_name.trim() || null,
      interaction: form.interaction,
      conversion_intent: form.conversion_intent,
      note: form.note.trim() || null,
    });
    const parts: string[] = [];
    parts.push(result.individual_created ? "contact created" : "contact reused");
    if (result.company_created) parts.push("company created");
    parts.push(`${result.followups_created} follow-ups queued`);
    setSuccessMsg(parts.join(" · "));
    // Reset the form for the next entry, but keep the dialog open so
    // Evelina can capture multiple leads in a row without re-tapping.
    setForm(EMPTY);
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <>
      <Tooltip title="Quick-add LinkedIn lead">
        <Fab
          color="secondary"
          aria-label="Quick-add LinkedIn lead"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: { xs: 20, md: 28 },
            right: { xs: 20, md: 28 },
            zIndex: (t) => t.zIndex.drawer + 2,
          }}
        >
          <LinkedInIcon />
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        // Phone-first: full-screen on xs so the keyboard doesn't crush the form.
        sx={{
          "& .MuiDialog-paper": {
            m: { xs: 0, sm: 4 },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
            height: { xs: "100%", sm: "auto" },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle>LinkedIn quick add</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {successMsg && (
              <Alert severity="success" onClose={() => setSuccessMsg(null)}>
                {successMsg}
              </Alert>
            )}
            {quickAdd.isError && (
              <Alert severity="error">
                {(quickAdd.error as { response?: { data?: { detail?: string } } })?.response?.data
                  ?.detail ?? "Quick-add failed. Try again."}
              </Alert>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="First name"
                value={form.first_name}
                onChange={(e) => update("first_name", e.target.value)}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="Last name"
                value={form.last_name}
                onChange={(e) => update("last_name", e.target.value)}
                fullWidth
                required
              />
            </Stack>

            <TextField
              label="LinkedIn URL"
              value={form.linkedin_url}
              onChange={(e) => update("linkedin_url", e.target.value)}
              fullWidth
              placeholder="https://www.linkedin.com/in/..."
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                fullWidth
                type="email"
              />
              <TextField
                label="Company"
                value={form.company_name}
                onChange={(e) => update("company_name", e.target.value)}
                fullWidth
              />
            </Stack>

            <FormControl>
              <FormLabel>How did this come in?</FormLabel>
              <RadioGroup
                value={form.interaction}
                onChange={(e) => update("interaction", e.target.value)}
              >
                {INTERACTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel>What's the conversion target?</FormLabel>
              <RadioGroup
                value={form.conversion_intent}
                onChange={(e) => update("conversion_intent", e.target.value)}
              >
                {INTENTS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <TextField
              label="Note (optional)"
              value={form.note}
              onChange={(e) => update("note", e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g. commented on the team-alignment post; mentioned their leadership team is fragmented"
            />

            <Box>
              <Typography variant="caption" color="text.secondary">
                A 3-touch follow-up sequence will be queued automatically. You'll find the tasks
                under CRM → Follow-ups.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {quickAdd.isPending ? "Saving…" : "Save lead"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
